import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { Stage } from 'konva/lib/Stage'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { Observer, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { Group, Image, Layer, Stage as StageComponent } from 'react-konva'
import { MapProps } from 'react-map-gl/mapbox'
import { AutoSizer } from 'react-virtualized'

import { IPosition, IProjectAddress, LocationType } from '~/client/graph'
import {
  IMapEvents,
  MapBoxViewer,
} from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer'
import { MapControls } from '~/client/src/shared/components/MapControls'
import MapBoxViewerAdapter from '~/client/src/shared/components/MapControls/MapBoxViewerAdapter'
import {
  hasPendingIconArt,
  iconArtSettled,
  type LocationIconName,
} from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import { SCALED_IMAGE_CONFIG } from '~/client/src/shared/constants/scaledImage'
import {
  CLOSED_BADGE_SPEC,
  PIN_SELECTED_SCALE,
} from '~/client/src/shared/map/markerGeometry'
import { NOOP } from '~/client/src/shared/utils/noop'

import Localization from '../../localization/LocalizationManager'
import GlobeView from '../../models/GlobeView'
import Sitemap from '../../models/Sitemap'
import InitialState from '../../stores/InitialState'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import MapBoxViewerStore from '../MapBoxViewer/MapBoxViewer.store'
import MapViewItemBase from '../SitemapHelpers/models/MapViewItemBase'

import './BaseMapView.scss'

interface ISitemapSizes {
  width: number
  height: number
  offsetX: number
  offsetY: number
  offsetXImage: number
  offsetYImage: number
}

interface IDataUrlConfig {
  x?: number
  y?: number
  width?: number
  height?: number
  pixelRatio?: number
  mimeType?: string
  quality?: number
  callback?: (str: string) => void
}

const DEFAULT_ZOOM_VALUE = 100
const SPACE_KEY_CODE = 32
const ITEMS_LAYER_ID = 'items-layer'
const ZOOM_SCALE = 0.95
const MAX_SCALE = 10
const MIN_SCALE = 0.05

interface IProps {
  zoom?: number
  sitemapUrl?: string
  getEditableItem?: (width: number, height: number, stage) => React.ReactNode
  onWhiteboardClick?: (evt?: KonvaEventObject<MouseEvent>) => void
  onTouch?: () => void
  children?: (bounds: {
    width: number
    height: number
    offsetX?: number
    offsetY?: number
    key?: string
  }) => JSX.Element
  shouldUseFullHeight?: boolean
  isDraggable?: boolean
  isEditorMode?: boolean

  selectedSitemap?: Sitemap
  opacity?: number
  mapBoxViewerStore: MapBoxViewerStore
  projectDateStore?: ProjectDateStore
  appState?: InitialState

  saveSitemapItem?: (item: MapViewItemBase) => Promise<void>
  selectedMapViewItem?: MapViewItemBase
  creatableAttributeType?: LocationType
  creatableAttributeIcon?: LocationIconName

  isLogisticsView?: boolean

  shouldHideControls?: boolean
  createAttributeInPosition?: (position: IPosition) => void

  globe?: GlobeView
  isDeliveryView?: boolean
  projectAddress: IProjectAddress
  isTextStickerCreationActive?: boolean
  createMapImage?: () => Promise<string>

  mapEvents?: IMapEvents
  isMobileEnv?: boolean
  isRubberMode?: boolean
  renderCenterMarker?: () => JSX.Element
  disableMapInteractions?: boolean
  // A host-owned basemap: the globe's saved style is then never applied.
  mapStyleOverride?: MapProps['mapStyle']
  isPitchLocked?: boolean

  // Element the map's Fullscreen control sends fullscreen, when the map itself
  // shouldn't be the target (e.g. fullscreen a parent that also holds overlays).
  fullscreenTargetRef?: React.RefObject<HTMLElement>
  // A host's own map controls, placed above Layers.
  mapControlsExtra?: React.ReactNode
}

const DEFAULT_WIDTH = 800
const DEFAULT_HEIGHT = 600

// Read here rather than forwarded through the provider: MapViewsGallery publishes this on its own
// element, and a custom property substitutes where it is declared, not where it is used.
const MOBILE_CONTROLS_TOP = 'var(--map-controls-top, 64px)'

// TODO: create store and move there all the logic to make the component standalone library and make it easy to understand
@observer
export default class BaseMapView extends React.Component<IProps> {
  public static defaultProps = {
    onMount: NOOP,
  }
  @observable private sitemapImage: HTMLImageElement = null
  @observable private isScrollingByDraggingAllowed: boolean = false
  @observable private isScrollingByDraggingActive: boolean = false
  @observable private lastDist: number = 0
  @observable private lastCenter: IPosition = null
  @observable private isSingleTouchEvent: boolean = true

  private stage: Stage
  private imageLoadingPromise: Promise<void>
  private pendingLoad: { image: HTMLImageElement; settle: () => void } = null
  private loadGeneration = 0
  private mapControlsAdapter: MapBoxViewerAdapter = null
  private containerRef = React.createRef<HTMLDivElement>()
  private autoSizerRef = React.createRef<AutoSizer>()

  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    this.mapControlsAdapter = new MapBoxViewerAdapter(props.mapBoxViewerStore)
    makeObservable(this)
  }

  public componentDidMount(): void {
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('keyup', this.onKeyUp)
    document.addEventListener('mouseup', this.onMouseUp)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    runInAction(() => {
      this.sitemapImage = null
    })
    this.loadSitemap()
    this.adjustSitemapItems()
  }

  public componentWillUnmount(): void {
    this.cancelSitemapLoad()
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('keyup', this.onKeyUp)
    document.removeEventListener('mouseup', this.onMouseUp)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  }

  // Entering/leaving fullscreen resizes the map container, but AutoSizer's
  // resize detector doesn't re-fire, so the map keeps its prior width. Force a
  // re-measure once the fullscreen layout has settled.
  private onFullscreenChange = (): void => {
    requestAnimationFrame(() =>
      (
        this.autoSizerRef.current as unknown as { _onResize?: () => void }
      )?._onResize?.(),
    )
  }

  @action
  public componentDidUpdate(oldProps: IProps): void {
    if (this.props !== oldProps) {
      this.localProps = this.props
    }

    if (oldProps.sitemapUrl !== this.props.sitemapUrl) {
      this.sitemapImage = null
      this.loadSitemap()
    }

    if (oldProps.isRubberMode !== this.props.isRubberMode) {
      this.props.isRubberMode ? this.loadSitemap() : (this.sitemapImage = null)
    }
    this.applyDefaultZoom(oldProps?.zoom)
    this.adjustSitemapItems()
  }

  public getDataURL = async (): Promise<string> => {
    if (!this.stage) {
      return null
    }
    const generation = this.loadGeneration
    if (this.imageLoadingPromise) {
      await this.imageLoadingPromise
    }
    // A superseded load settles at once and takes the Stage with it, so what we checked
    // before awaiting may be gone.
    if (!this.stage || this.loadGeneration !== generation) {
      return null
    }

    if (this.props.globe) {
      return this.props.createMapImage?.()
    }

    // Icon art still loading would be exported as its stand-in: wait for the art, then for its
    // observer to redraw before the layer is read.
    if (hasPendingIconArt()) {
      await iconArtSettled()
      await new Promise(resolve => setTimeout(resolve))
      if (!this.stage || this.loadGeneration !== generation) {
        return null
      }
    }

    const layer = this.stage.getStage().findOne(`#${ITEMS_LAYER_ID}`)
    return layer.toDataURL(Object.assign({}, this.config))
  }

  public getItemsDataURL = async (): Promise<string> => {
    if (!this.stage) {
      return null
    }
    const generation = this.loadGeneration
    if (this.imageLoadingPromise) {
      await this.imageLoadingPromise
    }
    if (!this.stage || this.loadGeneration !== generation) {
      return null
    }
    const layer = this.stage.getStage().findOne(`#${ITEMS_LAYER_ID}`)
    if (!layer?.getLayer?.()?.children?.[1]) {
      return null
    }

    const targetLayer = layer.getLayer().children[1]
    const config = this.config
    if (!config || config.width <= 0 || config.height <= 0) {
      console.warn('Invalid canvas dimensions for getItemsDataURL:', config)
      return null
    }

    return targetLayer.toDataURL(Object.assign({}, config))
  }

  public render(): JSX.Element {
    if (!this.sitemapImage && !this.props.globe) {
      return null
    }
    // Read here, outside the Observer below: not through the deep-observable localProps, where
    // MobX would copy the element and its component type on every update and React would
    // remount the control each time.
    const { mapControlsExtra } = this.props
    const {
      getEditableItem,
      isEditorMode,
      isRubberMode,
      opacity,
      mapBoxViewerStore,
      isLogisticsView,
      shouldHideControls,
      isDeliveryView,
      projectAddress,
      creatableAttributeType,
      creatableAttributeIcon,
      isTextStickerCreationActive,
      onWhiteboardClick,
      createAttributeInPosition,
      zoom,
      isDraggable,
      children,
      globe,
      mapEvents,
    } = this.props

    const isGlobeMode = !!globe

    return (
      <MapControls.Provider
        store={this.mapControlsAdapter}
        bare
        containerRef={this.props.fullscreenTargetRef ?? this.containerRef}
        isMobile={this.localProps.isMobileEnv}
        anchor={this.localProps.isMobileEnv ? 'top' : 'bottom'}
      >
        <div
          ref={this.containerRef}
          className="full-width full-height relative base-sitemap"
        >
          <div
            className={classList({
              'delivery-sitemap full-width full-height': true,
              'overflow-hidden': !isEditorMode,
            })}
          >
            <AutoSizer ref={this.autoSizerRef}>
              {({ width: containerWidth, height: containerHeight, key }) => (
                <Observer>
                  {() => {
                    if (containerWidth <= 0 || containerHeight <= 0) {
                      return null
                    }

                    const { offsetX, offsetY, width, height } =
                      this.getSitemapSizes(containerHeight, containerWidth)

                    const shouldShowMapBox = isGlobeMode

                    const hideControls = shouldHideControls || !isGlobeMode
                    return (
                      <>
                        {/* referencing map */}
                        {!isGlobeMode && (
                          <StageComponent
                            width={containerWidth}
                            height={containerHeight}
                            onClick={onWhiteboardClick}
                            onWheel={!zoom && this.handleZooming}
                            onTouchMove={!zoom && this.handleMultiTouch}
                            onTouchEnd={!zoom && this.handleMultiTouchEnd}
                            draggable={isDraggable}
                            ref={this.setStage}
                            dragBoundFunc={this.dragBoundFunc}
                          >
                            <Layer
                              id={ITEMS_LAYER_ID}
                              offsetX={-offsetX}
                              offsetY={-offsetY}
                            >
                              <Image
                                width={width > 0 ? width : DEFAULT_WIDTH}
                                height={height > 0 ? height : DEFAULT_HEIGHT}
                                image={this.sitemapImage}
                              />
                              <Group>
                                {children &&
                                  width > 0 &&
                                  height > 0 &&
                                  children({
                                    width: Math.max(width, 1),
                                    height: Math.max(height, 1),
                                    offsetX: -offsetX,
                                    offsetY: -offsetY,
                                    key,
                                  })}
                              </Group>
                            </Layer>
                          </StageComponent>
                        )}
                        {isRubberMode && (
                          <StageComponent
                            width={containerWidth}
                            height={containerHeight}
                          >
                            <Layer
                              id={ITEMS_LAYER_ID}
                              offsetX={-offsetX}
                              offsetY={-offsetY}
                            >
                              <Image
                                width={width > 0 ? width : DEFAULT_WIDTH}
                                height={height > 0 ? height : DEFAULT_HEIGHT}
                                image={this.sitemapImage}
                              />
                            </Layer>
                          </StageComponent>
                        )}
                        {/* referenced maps */}
                        {shouldShowMapBox && (
                          <div
                            style={{
                              position: 'absolute',
                              zIndex: 1,
                              top: 0,
                            }}
                          >
                            <MapBoxViewer
                              globeId={globe?.id}
                              isDeliveryView={isDeliveryView}
                              store={mapBoxViewerStore}
                              creatableAttributeType={creatableAttributeType}
                              creatableAttributeIcon={creatableAttributeIcon}
                              isTextStickerCreationActive={
                                isTextStickerCreationActive
                              }
                              width={Math.max(width, 1)}
                              height={Math.max(height, 1)}
                              containerWidth={containerWidth}
                              containerHeight={containerHeight}
                              isRubberMode={isRubberMode}
                              mapOpacity={isRubberMode ? opacity / 100 : 1}
                              globeLayer={this.renderLayer}
                              isLogisticsView={isLogisticsView}
                              latitude={projectAddress.center?.lat}
                              longitude={projectAddress.center?.lng}
                              isGlobeMode={isGlobeMode}
                              onAddressChanged={
                                mapBoxViewerStore.onAddressChanged
                              }
                              mapEvents={mapEvents}
                              createAttributeInPosition={
                                createAttributeInPosition
                              }
                              language={Localization.state.currentLanguage}
                              sitemapWithBasemapsOnGlobe={
                                mapBoxViewerStore.sitemapWithBasemapsOnGlobe
                              }
                              renderCenterMarker={
                                this.localProps.renderCenterMarker
                              }
                              disableMapInteractions={
                                this.localProps.disableMapInteractions
                              }
                              mapStyleOverride={
                                this.localProps.mapStyleOverride
                              }
                              isPitchLocked={this.localProps.isPitchLocked}
                            />
                          </div>
                        )}
                        {shouldShowMapBox &&
                          !hideControls &&
                          this.renderMapControls(mapControlsExtra)}
                        {getEditableItem &&
                          getEditableItem(
                            Math.max(width, 1),
                            Math.max(height, 1),
                            this.stage,
                          )}
                      </>
                    )
                  }}
                </Observer>
              )}
            </AutoSizer>
          </div>
          {this.renderDraggableArea()}
        </div>
      </MapControls.Provider>
    )
  }

  private renderMapControls(mapControlsExtra: React.ReactNode): JSX.Element {
    const { isRubberMode, isMobileEnv } = this.localProps

    return (
      <MapControls.Chrome
        // Mobile: start below the MapViewsSelector pill (offset published as the
        // --map-controls-top CSS var by MapViewsGallery). Desktop: the bottom baseline.
        anchorInset={isMobileEnv ? MOBILE_CONTROLS_TOP : undefined}
        hasFullscreen={!isRubberMode}
        leftExtra={mapControlsExtra}
      />
    )
  }

  private dragBoundFunc = pos => {
    const IMAGE_OFFSET_X = 10
    const IMAGE_OFFSET_Y = 10
    const stage = this.stage.getStage()
    const width = stage.attrs.width
    const height = stage.attrs.height
    const scale = this.stage.scaleX()
    const offsetX = this.stage.getStage().children[0].attrs?.offsetX || 0
    const offsetY = this.stage.getStage().children[0].attrs?.offsetY || 0

    const canvasWidth =
      this.stage.getStage().children[0].children[0].attrs?.width
    const canvasHeight =
      this.stage.getStage().children[0].children[0].attrs?.height

    let newX = pos.x
    let newY = pos.y

    if (newX < scale * (offsetX - canvasWidth) + IMAGE_OFFSET_X) {
      newX = scale * (offsetX - canvasWidth) + IMAGE_OFFSET_X
    }
    if (newX > width + offsetX * scale - IMAGE_OFFSET_X) {
      newX = width + offsetX * scale - IMAGE_OFFSET_X
    }
    if (newY < scale * (offsetY - canvasHeight) + IMAGE_OFFSET_Y) {
      newY = scale * (offsetY - canvasHeight) + IMAGE_OFFSET_Y
    }
    if (newY > height + offsetY * scale - IMAGE_OFFSET_Y) {
      newY = height + offsetY * scale - IMAGE_OFFSET_Y
    }

    return {
      x: newX,
      y: newY,
    }
  }

  private renderDraggableArea(): JSX.Element {
    return (
      <div
        className={classList({
          'unclickable-element transparent': !this.isScrollingByDraggingAllowed,
          'absolute grab-area full-width full-height':
            this.isScrollingByDraggingAllowed,
          'grabbing-cursor': this.isScrollingByDraggingActive,
        })}
        onMouseDown={this.onGrabAreaMouseDown}
        onMouseMove={this.onGrabAreaMouseMove}
      />
    )
  }

  private renderLayer = (width: number, height: number): JSX.Element => {
    const { children } = this.localProps

    if (!children || width <= 0 || height <= 0) {
      return null
    }

    return children({
      width: Math.max(width, 1),
      height: Math.max(height, 1),
    })
  }

  private setStage = ref => {
    // Issue with updating stage
    this.stage = ref
  }

  // Detaches the handlers rather than just dropping the promise: the browser keeps downloading,
  // and a superseded onload would repaint a sitemap the view has already moved off.
  private cancelSitemapLoad(): void {
    this.loadGeneration++
    if (this.pendingLoad) {
      this.pendingLoad.image.onload = null
      this.pendingLoad.image.onerror = null
      this.pendingLoad.settle()
      this.pendingLoad = null
    }
    this.imageLoadingPromise = null
  }

  private loadSitemap(): void {
    this.cancelSitemapLoad()
    // No url on a globe view, and transiently before a basemap loads. Assigning it to img.src
    // stringifies to '/undefined' and 404s.
    if (!this.props.sitemapUrl) {
      return
    }
    this.imageLoadingPromise = new Promise(resolve => {
      const image = document.createElement('img')
      this.pendingLoad = { image, settle: resolve }
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = this.props.sitemapUrl
      image.onload = () => {
        this.pendingLoad = null
        if (image.naturalWidth > 0 && image.naturalHeight > 0) {
          runInAction(() => {
            this.sitemapImage = image
          })
          resolve()
          this.imageLoadingPromise = null
          this.setZeroPosition()
          this.resize(1)
        } else {
          console.warn('Loaded image has invalid dimensions:', {
            naturalWidth: image.naturalWidth,
            naturalHeight: image.naturalHeight,
            src: image.src,
          })
          resolve()
          this.imageLoadingPromise = null
        }
      }

      image.onerror = () => {
        this.pendingLoad = null
        console.error('Failed to load sitemap image:', image.src)
        resolve()
        this.imageLoadingPromise = null
      }
    })
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.props.globe) {
      return
    }
    switch (event.keyCode) {
      case SPACE_KEY_CODE:
        this.isScrollingByDraggingAllowed = true
        break
    }
  }

  @action.bound
  private onKeyUp = (event: KeyboardEvent): void => {
    if (this.props.globe) {
      return
    }
    switch (event.keyCode) {
      case SPACE_KEY_CODE:
        this.isScrollingByDraggingAllowed = false
        this.isScrollingByDraggingActive = false
        break
    }
  }

  @action.bound
  private onMouseUp = (): void => {
    if (this.props.globe) {
      return
    }
    this.isScrollingByDraggingActive = false
  }

  @action.bound
  private onGrabAreaMouseDown = (): void => {
    if (this.props.globe) {
      return
    }
    this.isScrollingByDraggingActive = true
  }

  private onGrabAreaMouseMove = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (
      !this.isScrollingByDraggingAllowed ||
      !this.isScrollingByDraggingActive ||
      !this.stage
    ) {
      return
    }
    const stage = this.stage.getStage()
    const scrollLeft = stage.x() + event.movementX
    const scrollTop = stage.y() + event.movementY

    stage.x(scrollLeft)
    stage.y(scrollTop)
    stage.batchDraw()
  }

  private adjustSitemapItems = (): void => {
    if (this.stage) {
      this.resize(this.stage.getStage().scaleX(), null, false, true)
    }
  }

  // canvas zooming handling
  private handleZooming = (event: KonvaEventObject<WheelEvent>): void => {
    event.evt.preventDefault()

    const stage = this.stage.getStage()
    const oldScale = stage.scaleX()
    const mousePointTo = {
      x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
      y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
    }

    const newScale =
      event.evt.deltaY > 0 ? oldScale * ZOOM_SCALE : oldScale / ZOOM_SCALE

    const newPosition = {
      x: -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale,
      y: -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale,
    }

    this.resize(newScale, newPosition)
  }

  // reset canvas zooming
  private setZeroPosition(): void {
    if (!this.stage) {
      return
    }
    const stage = this.stage.getStage()
    stage.x(0)
    stage.y(0)
  }

  // canvas multi touch like mobile finger action
  private handleMultiTouch = (event: KonvaEventObject<TouchEvent>): void => {
    event.evt.preventDefault()
    const stage = this.stage.getStage()
    // in order for react konva to work properly with multi touch dragging should be disabled
    stage.stopDrag()

    const { touches } = event.evt

    if (touches[0] && touches[1]) {
      this.isSingleTouchEvent = false
      const point1 = {
        x: touches[0].clientX,
        y: touches[0].clientY,
      }
      const point2 = {
        x: touches[1].clientX,
        y: touches[1].clientY,
      }
      const newCenter = this.getCenter(point1, point2)

      if (!this.lastCenter) {
        this.lastCenter = newCenter
        return
      }

      const dist = this.getDistance(point1, point2)
      if (!this.lastDist) {
        this.lastDist = dist
      }
      const scale = stage.scaleX() * (dist / this.lastDist)
      const offsetX = newCenter.x - this.lastCenter.x
      const offsetY = newCenter.y - this.lastCenter.y

      const pointTo = {
        x: (newCenter.x - stage.x()) / stage.scaleX(),
        y: (newCenter.y - stage.y()) / stage.scaleX(),
      }
      const newPosition = {
        x: newCenter.x - pointTo.x * scale + offsetX,
        y: newCenter.y - pointTo.y * scale + offsetY,
      }

      this.resize(scale, newPosition)

      this.lastDist = dist
      this.lastCenter = newCenter
    } else {
      stage.startDrag()
    }
  }

  private handleMultiTouchEnd = (): void => {
    const { onTouch } = this.props
    this.lastDist = 0
    this.lastCenter = null

    if (this.isSingleTouchEvent && onTouch) {
      onTouch()
    }
    this.isSingleTouchEvent = true
  }

  private getDistance(point1: IPosition, point2: IPosition): number {
    return Math.sqrt(
      Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2),
    )
  }

  private getCenter(point1: IPosition, point2: IPosition): IPosition {
    return {
      x: (point1.x + point2.x) / 2,
      y: (point1.y + point2.y) / 2,
    }
  }

  private applyDefaultZoom = (oldZoom: number): void => {
    const { zoom } = this.props
    if (!zoom) {
      return
    }

    this.resize(zoom / DEFAULT_ZOOM_VALUE, null, true)

    if (oldZoom !== zoom && zoom === DEFAULT_ZOOM_VALUE) {
      this.setZeroPosition()
    }
  }

  private getSitemapSizes(
    containerHeight: number,
    containerWidth: number,
  ): ISitemapSizes {
    let width = containerWidth
    let height = containerHeight
    const { mapBoxViewerStore } = this.localProps

    if (containerWidth <= 0 || containerHeight <= 0) {
      const defaultWidth = DEFAULT_WIDTH
      const defaultHeight = DEFAULT_HEIGHT
      return {
        offsetX: 0,
        offsetY: 0,
        width: defaultWidth,
        height: defaultHeight,
        offsetXImage: 0,
        offsetYImage: 0,
      }
    }

    if (this.sitemapImage) {
      const { naturalWidth, naturalHeight } = this.sitemapImage

      if (naturalWidth <= 0 || naturalHeight <= 0) {
        width = containerWidth
        height = containerHeight
      } else {
        const imageScale = Math.min(
          containerHeight / naturalHeight,
          containerWidth / naturalWidth,
        )

        width = naturalWidth * imageScale
        height = naturalHeight * imageScale

        if (width <= 0) width = containerWidth
        if (height <= 0) height = containerHeight
      }
    }

    const offsetX = Math.max(0, (containerWidth - width) / 2)

    const offsetY = Math.max(0, (containerHeight - height) / 2)

    const offsetXImage = Math.max(0, (containerWidth - width) / 2)
    const offsetYImage = Math.max(0, (containerHeight - height) / 2)
    mapBoxViewerStore.width = width
    mapBoxViewerStore.height = height
    mapBoxViewerStore.offsetX = offsetX
    mapBoxViewerStore.offsetY = offsetY

    return {
      offsetX,
      offsetY,
      width,
      height,
      offsetXImage,
      offsetYImage,
    }
  }

  private get config(): IDataUrlConfig {
    const { selectedSitemap } = this.localProps
    const layer = this.stage.getStage().findOne(`#${ITEMS_LAYER_ID}`)
    let width
    let height
    let y
    let x

    if (selectedSitemap?.isReferenced || !selectedSitemap) {
      const referencedLayer = (layer as any).children[0]

      width = referencedLayer.width()
      height = referencedLayer.height()
      y = referencedLayer.attrs.y
      x = referencedLayer.attrs.x
    } else {
      const scale = this.stage.getStage().scaleX()
      const offsetX = layer.attrs.offsetX
      const offsetY = layer.attrs.offsetY

      width = (layer.width() + offsetX * 2) * scale
      height = (layer.height() + offsetY * 2) * scale
      x = this.stage.getStage().x() - offsetX * scale
      y = this.stage.getStage().y() - offsetY * scale
    }

    return {
      ...SCALED_IMAGE_CONFIG,
      width,
      height,
      x,
      y,
    } as IDataUrlConfig
  }

  private resize(
    newScale: number,
    newPosition?: IPosition,
    shouldOnlyFirstLayer?: boolean,
    shouldIgnoreStage?: boolean,
  ): void {
    if (newScale > MAX_SCALE || newScale < MIN_SCALE || !this.stage) {
      return
    }

    const stage = this.stage.getStage()

    if (!(stage.getLayers()[0]?.children[1] as any)?.children) {
      return
    }

    const itemLayers = (stage.getLayers()[0] as any).children[1].children
    let sitemapItemLayers = Array.isArray(itemLayers)
      ? itemLayers
      : itemLayers.toArray()

    if (shouldOnlyFirstLayer) {
      sitemapItemLayers = sitemapItemLayers.slice(0, 1)
    }

    if (!shouldIgnoreStage) {
      // resize stage
      stage.scaleX(newScale)
      stage.scaleY(newScale)
    }

    // resize stage items
    sitemapItemLayers.forEach(layer => {
      const layerChildren = layer.children
      const childrenArray = Array.isArray(layerChildren) ? layerChildren : []
      childrenArray
        .filter(child => !child.attrs.isStaticSize)
        .forEach(child => {
          let baseScale = 1
          if (child.attrs.isClosedIcon) {
            baseScale = CLOSED_BADGE_SPEC.konvaScale
          } else if (child.attrs.isSelected) {
            baseScale = PIN_SELECTED_SCALE
          }

          child.scaleY(baseScale / newScale)
          child.scaleX(baseScale / newScale)
        })
    })

    if (newPosition) {
      stage.position(newPosition)
    }
    stage.batchDraw()
  }
}
